import { useState, useCallback } from 'react';
import backendService from '../services/backendService';

// Custom hook for backend API operations
export const useBackend = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Generic API call wrapper
  const callAPI = useCallback(async (apiFunction, ...args) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiFunction(...args);
      setLoading(false);
      
      if (result.success) {
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  }, []);

  // User Management
  const getUserProfile = useCallback(() => {
    return callAPI(backendService.getUserProfile);
  }, [callAPI]);

  const updateUserProfile = useCallback((userData) => {
    return callAPI(backendService.updateUserProfile, userData);
  }, [callAPI]);

  const getAllUsers = useCallback(() => {
    return callAPI(backendService.getAllUsers);
  }, [callAPI]);

  // AI Agents
  const getUserAgents = useCallback(() => {
    return callAPI(backendService.getUserAgents);
  }, [callAPI]);

  const createAgent = useCallback((agentData) => {
    return callAPI(backendService.createAgent, agentData);
  }, [callAPI]);

  const updateAgent = useCallback((id, agentData) => {
    return callAPI(backendService.updateAgent, id, agentData);
  }, [callAPI]);

  const deleteAgent = useCallback((id) => {
    return callAPI(backendService.deleteAgent, id);
  }, [callAPI]);

  // Workflows
  const getUserWorkflows = useCallback(() => {
    return callAPI(backendService.getUserWorkflows);
  }, [callAPI]);

  const createWorkflow = useCallback((workflowData) => {
    return callAPI(backendService.createWorkflow, workflowData);
  }, [callAPI]);

  const updateWorkflow = useCallback((id, workflowData) => {
    return callAPI(backendService.updateWorkflow, id, workflowData);
  }, [callAPI]);

  const executeWorkflow = useCallback((id) => {
    return callAPI(backendService.executeWorkflow, id);
  }, [callAPI]);

  const deleteWorkflow = useCallback((id) => {
    return callAPI(backendService.deleteWorkflow, id);
  }, [callAPI]);

  // Services
  const getPublicServices = useCallback(() => {
    return callAPI(backendService.getPublicServices);
  }, [callAPI]);

  const getServiceById = useCallback((id) => {
    return callAPI(backendService.getServiceById, id);
  }, [callAPI]);

  const deployService = useCallback((id) => {
    return callAPI(backendService.deployService, id);
  }, [callAPI]);

  // Dashboard
  const getUserStats = useCallback(() => {
    return callAPI(backendService.getUserStats);
  }, [callAPI]);

  const getAdminStats = useCallback(() => {
    return callAPI(backendService.getAdminStats);
  }, [callAPI]);

  // API Keys
  const getUserApiKeys = useCallback(() => {
    return callAPI(backendService.getUserApiKeys);
  }, [callAPI]);

  const createApiKey = useCallback((keyData) => {
    return callAPI(backendService.createApiKey, keyData);
  }, [callAPI]);

  const deleteApiKey = useCallback((id) => {
    return callAPI(backendService.deleteApiKey, id);
  }, [callAPI]);

  // Test connection
  const testConnection = useCallback(() => {
    return callAPI(backendService.testConnection);
  }, [callAPI]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    loading,
    error,
    clearError,
    
    // User Management
    getUserProfile,
    updateUserProfile,
    getAllUsers,
    
    // AI Agents
    getUserAgents,
    createAgent,
    updateAgent,
    deleteAgent,
    
    // Workflows
    getUserWorkflows,
    createWorkflow,
    updateWorkflow,
    executeWorkflow,
    deleteWorkflow,
    
    // Services
    getPublicServices,
    getServiceById,
    deployService,
    
    // Dashboard
    getUserStats,
    getAdminStats,
    
    // API Keys
    getUserApiKeys,
    createApiKey,
    deleteApiKey,
    
    // Utilities
    testConnection,
  };
};

export default useBackend;